import React, { useState } from "react";
import { IonApp, IonRouterOutlet, useIonViewWillEnter, useIonViewWillLeave } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { Route, Redirect, useHistory, RouteProps } from "react-router";
import loadable from "@loadable/component";
import { Provider } from "react-redux";
import { QueryClient, QueryClientProvider } from "react-query";
import queryString from "query-string";
import { createBrowserHistory } from "history";

import { store } from "./store";
import { refreshToken } from "./features/auth/auth-slice";
import * as _cio from "./lib/customer-io";
import ReactGA from "react-ga4";
import ReactPixel from "react-facebook-pixel";

import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";

/** Import Styles */
import "./styles/base.css";
import GlobalStyles from "./styles/GlobalStyles";

/** Components */
import { TabsNavigation } from "./components/layout";

/** Stripe */
import useAuth from "./hooks/useAuth";
import OnboardingCreateListing from "./pages/OnboardingCreateListing";

/** Pages */
const NotFound = loadable(() => import("./pages/NotFound"));
const Login = loadable(() => import("./pages/Login"));
const ResetPasswordRequest = loadable(() => import("./pages/ResetPasswordRequest"));
const ResetPassword = loadable(() => import("./pages/ResetPassword"));
const VerifyEmail = loadable(() => import("./pages/VerifyEmail"));
const Chat = loadable(() => import("./pages/Chat"));
const PublicProfile = loadable(() => import("./pages/PublicProfile"));
const UserProfile = loadable(() => import("./pages/MyAccount/UserProfile"));
const PersonalInformation = loadable(() => import("./pages/MyAccount/PersonalInformation"));
const RentalHistory = loadable(() => import("./pages/MyAccount/RentalHistory"));
const ManageBookings = loadable(() => import("./pages/MyAccount/ManageBookings"));
const ManageListings = loadable(() => import("./pages/MyAccount/ManageListings"));
const Payments = loadable(() => import("./pages/MyAccount/Payments"));
const CreateListing = loadable(() => import("./pages/CreateListing"));
const ManageListing = loadable(() => import("./pages/ManageListing"));
const Categories = loadable(() => import("./pages/Categories"));
const About = loadable(() => import("./pages/About"));
const Contact = loadable(() => import("./pages/Contact"));
const Dispute = loadable(() => import("./pages/Dispute"));
const Competition = loadable(() => import("./pages/Competition"));

/** Contexts */
export const queryClient = new QueryClient();
export const routerHistory = createBrowserHistory();

/** Init Tracking */

Sentry.init({
  dsn: "https://6703d8b0b58e4465a4a0adfb72aa557f@o1014701.ingest.sentry.io/5979936",
  integrations: [
    new Integrations.BrowserTracing({
      routingInstrumentation: Sentry.reactRouterV5Instrumentation(routerHistory),
      tracingOrigins: [
        "localhost",
        "api.releaseit.com.au",
        "staging-api.releaseit.com.au",
        "dev-api.releaseit.com.au",
      ],
    }),
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
});

function initGA() {
  // Google Analytics
  // all this env var to be comma separated
  const trackingIds = (process.env.REACT_APP_GA_ID as string).split(/\s*,\s*/).map((trackingId) => {
    return { trackingId, gtagOptions: { user_id: store.getState().auth.user?._id } };
  });

  // the below fires a typescript error, but the documentation itself literally gives this example https://www.npmjs.com/package/react-ga4
  // it has also been tested to work so I feel it's an issue of the types not being updated in v1.1.1 (it was only just added at time of adding package)
  // @ts-ignore
  ReactGA.initialize(trackingIds);
}

// Facebook
ReactPixel.init(process.env.REACT_APP_FB_ID as string);

/** Auth Bootstrap */

store.dispatch(refreshToken()).finally(() => {
  initGA();
});

const App: React.FC = () => (
  <>
    <GlobalStyles />

    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <IonApp>
          <IonReactRouter history={routerHistory}>
            <Routes />
            <TabsNavigation className="ion-hide-lg-up" />
          </IonReactRouter>
        </IonApp>
      </QueryClientProvider>
    </Provider>
  </>
);

function ProtectedRoute({
  component: Component,
  ...props
}: RouteProps & { component: React.ComponentType }) {
  const { user, loading } = useAuth();

  const [isFront, setIsFront] = useState(true);
  useIonViewWillEnter(() => {
    setIsFront(true);
  });
  useIonViewWillLeave(() => {
    setIsFront(false);
  });

  const url = React.useRef();
  url.current = url.current || (props as any)?.computedMatch?.url;

  if (isFront && !user && !loading) {
    return (
      <Redirect
        to={{
          pathname: "/login",
          search: `?callbackUrl=${url.current}`,
        }}
      />
    );
  }

  if (loading) {
    return null;
  }

  return <Route {...props} render={(params) => <Component {...params} />} />;
}

declare global {
  interface Window {
    _tfa: any;
  }
}

const Routes: React.FC = () => {
  const history = useHistory();

  // Track page views
  React.useEffect(() => {
    const unsubscribe = history.listen((location) => {
      _cio.trackPageView({ pageType: location.pathname.substr(1) });
      ReactPixel.pageView();
      window._tfa.push({ notify: "event", name: "page_view" });

      // I tried to use useLocation() here but React it won't allow it
      const { q } = queryString.parse(window.location.search);
      if (q) {
        ReactGA.event("view_search_results", { search_term: q });
      } else {
        ReactGA.send("pageview");
      }
    });

    return unsubscribe;
  });

  return (
    <IonRouterOutlet animated={false} id="main">
      {/* Paths */}
      <Route exact path="/login" component={Login} />
      <Route exact path="/reset-password" component={ResetPasswordRequest} />
      <Route path="/reset-password/:resetToken" component={ResetPassword} />
      <Route path="/verify-email/:verifyToken" component={VerifyEmail} />
      <Route exact path="/about" component={About} />
      <Route exact path="/contact" component={Contact} />
      <Route exact path="/competition" component={Competition} />
      <ProtectedRoute exact path="/chat" component={Chat} />
      <ProtectedRoute path="/chat/:listingId" component={Chat} />
      <Route exact path="/category" component={Categories} />
      <ProtectedRoute exact path="/my-account/profile" component={UserProfile} />
      <ProtectedRoute exact path="/my-account/personal-info" component={PersonalInformation} />
      <ProtectedRoute exact path="/my-account/rental-history" component={RentalHistory} />
      <ProtectedRoute exact path="/my-account/manage-bookings" component={ManageBookings} />
      <ProtectedRoute exact path="/my-account/manage-listings" component={ManageListings} />
      <ProtectedRoute exact path="/my-account/payments" component={Payments} />
      <ProtectedRoute exact path="/onboarding/create-listing" component={OnboardingCreateListing} />
      <ProtectedRoute exact path="/create-listing" component={CreateListing} />
      <ProtectedRoute exact path="/manage-listing/:id" component={ManageListing} />
      <Route path="/profile/:userId" component={PublicProfile} />
      <Redirect exact path="/dispute" to="/page/dispute" />
      <ProtectedRoute path="/dispute/:bookingId" component={Dispute} />
      <Redirect exact path="/faqs" to="/page/faqs" />

      {/* Redirects */}
      <Route component={NotFound} />
    </IonRouterOutlet>
  );
};

export default App;
